import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  Box,
  TextField,
  Typography,
  Divider,
  Autocomplete,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Button,
  Chip,
  Stack,
  Paper,
  Fade,
} from '@mui/material'

import { LoadingButton } from '@mui/lab'

import Collapse from '@mui/material/Collapse'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'

import { rulesService } from '../../../services/rulesService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { events, examples, fns } from './data'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useConfirmAction } from '../../../hooks/useConfirmAction'
import { toast } from 'react-toastify'
import FunctionList from './FunctionList'
import { getTimeInMillis } from '../../../utility/datetime'
import DSLMonacoEditor from '../../../components/DSLMonacoEditor'

const cardSX = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 4,
  bgcolor: 'background.paper',
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
}

const RuleEditorDialog = ({
  open,
  onClose,

  processTemplateId,
  activityTemplateId,

  allRules = [],
  activities = [],

  processId,
}) => {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const { confirm, ConfirmDialog } = useConfirmAction()

  const isCentralizedMode = !processTemplateId || !activityTemplateId

  const [selectedProcessId, setSelectedProcessId] = useState(processTemplateId || '')
  const [selectedActivityId, setSelectedActivityId] = useState(activityTemplateId || '')

  const effectiveProcessTemplateId = selectedProcessId || processTemplateId
  const effectiveActivityTemplateId = selectedActivityId || activityTemplateId

  const [dsl, setDsl] = useState('')
  const [parsedJson, setParsedJson] = useState(null)

  const [isValidating, setIsValidating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [generatingDSL, setGeneratingDSL] = useState(false)

  const [error, setError] = useState(null)
  const [isValid, setIsValid] = useState(false)

  const [rules, setRules] = useState([])

  const [selectedRuleId, setSelectedRuleId] = useState(null)
  const [isEditMode, setIsEditMode] = useState(false)

  const [ruleName, setRuleName] = useState('')
  const [trigger, setTrigger] = useState(events[0] || '')
  const [expiryDateTime, setExpiryDateTime] = useState('')

  const [rightTab, setRightTab] = useState(0)

  const [variables, setVariables] = useState([])
  const [routes, setRoutes] = useState([])
  const [selectedCategoryId, setSelectedCategoryId] = useState('')

  const [prompts, setPrompts] = useState('')
  const [timeTaken, setTimeTaken] = useState(0)

  const generateAbortRef = useRef(null)

  const [showConfiguration, setShowConfiguration] = useState(false)
  const [dslGenError, setDslGenError] = useState(null)

  const ready = Number(effectiveProcessTemplateId) > 0 && Number(effectiveActivityTemplateId) > 0

  // =====================================================
  // OPTIONS
  // =====================================================

  const categoryOptions = useMemo(() => {
    const map = new Map()

    activities.forEach((item) => {
      if (!item.category_id) return

      map.set(item.category_id, {
        id: item.category_id,
        name: item.category_name,
      })
    })

    return Array.from(map.values())
  }, [activities])

  const processOptions = useMemo(() => {
    const map = new Map()

    activities
      .filter((item) => {
        if (!selectedCategoryId) return true
        return Number(item.category_id) === Number(selectedCategoryId)
      })
      .forEach((item) => {
        map.set(item.process_template_id, {
          id: item.process_template_id,
          name: item.process_template_name,
        })
      })

    return Array.from(map.values())
  }, [activities, selectedCategoryId])

  const activityOptions = useMemo(() => {
    return activities
      .filter((item) => Number(item.process_template_id) === Number(selectedProcessId))
      .map((item) => ({
        id: item.activity_template_id,
        name: `${item.activity_template_name} (${item.activity_type})`,
      }))
  }, [activities, selectedProcessId])

  // =====================================================
  // RESET
  // =====================================================

  const resetEditor = () => {
    setDsl('')
    setParsedJson(null)
    setError(null)
    setIsValid(false)
    setRuleName('')
    setTrigger(events[0])
    setExpiryDateTime('')
    setSelectedRuleId(null)
    setIsEditMode(false)
    setPrompts('')
  }

  // =====================================================
  // EDIT
  // =====================================================

  const handleEdit = (rule) => {
    setDsl(rule.dsl)
    setRuleName(rule.name)
    setTrigger(rule.triggers?.[0] || events[0])
    setSelectedRuleId(rule.id)
    setIsEditMode(true)
    setIsValid(true)

    setExpiryDateTime(rule.valid_till ? new Date(rule.valid_till).toISOString() : '')
  }

  // =====================================================
  // FETCH RULES
  // =====================================================

  const fetchRules = async () => {
    if (!ready) return

    try {
      let fetchedRules = []

      if (allRules?.length) {
        fetchedRules = allRules.filter(
          (rule) =>
            Number(rule.process_template_id) === Number(effectiveProcessTemplateId) &&
            Number(rule.activity_template_id) === Number(effectiveActivityTemplateId),
        )
      } else {
        fetchedRules = await rulesService.getRules(
          user,
          effectiveProcessTemplateId,
          effectiveActivityTemplateId,
        )
      }

      setRules(fetchedRules)

      const existingRule = fetchedRules.find((rule) => Number(rule.created_by) === Number(user?.id))

      if (existingRule) {
        handleEdit(existingRule)
      } else {
        resetEditor()
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to fetch rules')
    }
  }

  // =====================================================
  // VARIABLES
  // =====================================================

  const getProcessDataVariableKeys = async () => {
    if (!ready) return

    try {
      const { keys, routes } = await rulesService.getProcessDataVariableKeys(
        user,
        effectiveActivityTemplateId,
      )

      setVariables(keys.map((it) => it.key))
      setRoutes(routes.map((it) => it.route_template_name))
    } catch (err) {
      setVariables([])
      setRoutes([])
    }
  }

  // =====================================================
  // LOAD
  // =====================================================

  useEffect(() => {
    if (!open) {
      resetEditor()
      return
    }

    if (ready) {
      fetchRules()
      getProcessDataVariableKeys()
    }
  }, [open, effectiveProcessTemplateId, effectiveActivityTemplateId])

  // =====================================================
  // VALIDATE
  // =====================================================

  const handleValidate = async () => {
    try {
      setIsValidating(true)
      setError(null)

      const res = await rulesService.validateRule(user, {
        dsl,
        processTemplateId: effectiveProcessTemplateId,
        activityTemplateId: effectiveActivityTemplateId,
      })

      setParsedJson(res.ast)
      setIsValid(true)

      toast.success('Validated successfully')
    } catch (err) {
      setError(err?.response?.data?.error || 'Validation failed')
      setIsValid(false)

      toast.error(err?.response?.data?.error || 'Validation failed')
    } finally {
      setIsValidating(false)
    }
  }

  // =====================================================
  // SAVE
  // =====================================================

  const handleSave = async () => {
    try {
      setIsSaving(true)

      const payload = {
        name: ruleName || 'Untitled Rule',
        dsl,
        triggers: trigger ? [trigger] : [],
        processTemplateId: effectiveProcessTemplateId,
        activityTemplateId: effectiveActivityTemplateId,
        priority: 1,
        stopOnMatch: true,
        createdBy: user?.id,
        validTill: expiryDateTime || null,
      }

      if (isEditMode && selectedRuleId) {
        await rulesService.updateRule(user, selectedRuleId, payload)
      } else {
        await rulesService.createRule(user, payload)
      }

      toast.success('Saved successfully')

      await fetchRules()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Save failed')
    } finally {
      setIsSaving(false)
    }
  }

  // =====================================================
  // DELETE
  // =====================================================

  const handleDelete = async (ruleId) => {
    try {
      await rulesService.deleteRule(user, ruleId)

      toast.success('Deleted successfully')

      resetEditor()

      await fetchRules()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Delete failed')
    }
  }

  // =====================================================
  // RUN
  // =====================================================

  const handleRunRule = async (ruleId) => {
    confirm({
      heading: 'Confirm Execution',
      message: 'Please save changes before running the rule.',
      confirmText: 'Run',

      actionFn: async () => {
        try {
          await rulesService.triggerRule(user, ruleId, processId)

          toast.success('Run successfully')

          setTimeout(() => navigate(-1), 2000)
        } catch (err) {
          toast.error(err?.response?.data?.error || 'Execution failed')
        }
      },
    })
  }

  // =====================================================
  // GENERATE DSL
  // =====================================================

  const generateDSLOfPrompts = async () => {
    setDslGenError(null)
    const controller = new AbortController()

    generateAbortRef.current = controller

    setGeneratingDSL(true)
    setTimeTaken(0)

    const startTime = getTimeInMillis()

    try {
      const data = {
        userPrompt: prompts,
        processTemplateId: effectiveProcessTemplateId,
        activityTemplateId: effectiveActivityTemplateId,
        fields: variables,
        votes: routes,
      }

      const res = await rulesService.generateDSLofPrompt(user, data, controller.signal)

      if (res.dsl) {
        setDsl(res.dsl)
        setIsValid(false)
        setTimeTaken((getTimeInMillis() - startTime) / 1000)
      }
    } catch (error) {
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        toast.info('Generation cancelled')
      } else {
        setDslGenError('Oops, AI assist is not available. Please try again later!')
        toast.error(
          'Oops, AI assist is not available. Please try again later!' || 'Generation failed',
        )
      }
    } finally {
      setGeneratingDSL(false)
      generateAbortRef.current = null
    }
  }

  const cancelGenerateDSL = () => {
    if (generateAbortRef.current) {
      generateAbortRef.current.abort()
    }
  }

  // =====================================================
  // HELPERS
  // =====================================================

  const copyVariable = async (value) => {
    await navigator.clipboard.writeText(value)
    toast.success('Copied')
  }

  const formatDSL = async (dsl) => {
    try {
      const formattedDSL = await rulesService.formatDSL(user, dsl)

      setDsl(formattedDSL.dsl)
    } catch (err) {
      toast.error(err.message || 'Formatter failed')
    }
  }

  // =====================================================
  // UI
  // =====================================================

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          setDsl('')
          setSelectedCategoryId(null)
          setSelectedProcessId(null)
          setSelectedActivityId(null)
          setPrompts('')
          setVariables([])
          setRoutes([])
          onClose(false)
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '96vw',
            height: '90vh',
            borderRadius: 4,
            overflow: 'hidden',
          },
        }}
      >
        {/* ===================================================== */}
        {/* HEADER */}
        {/* ===================================================== */}

        <Box
          px={2}
          py={1.2}
          borderBottom="1px solid"
          borderColor="divider"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          bgcolor="background.paper"
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Rule Editor
            </Typography>

            <Typography variant="body2" color="text.secondary">
              AI-assisted workflow DSL builder
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            {isEditMode && <Chip label="Edit Mode" color="warning" size="small" />}

            {isValid && (
              <Chip icon={<CheckCircleIcon />} label="Validated" color="success" size="small" />
            )}
          </Stack>
        </Box>

        {/* ===================================================== */}
        {/* CONTENT */}
        {/* ===================================================== */}

        <Box
          p={2}
          display="flex"
          flexDirection="column"
          gap={1}
          sx={{
            flex: 1,
            overflow: 'hidden',
            bgcolor: '#f8fafc',
          }}
        >
          <Paper
            sx={{
              ...cardSX,
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {/* HEADER */}

            <Box
              px={2}
              py={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                cursor: 'pointer',
                bgcolor: '#fafafa',
                borderBottom: showConfiguration ? '1px solid #eee' : 'none',
              }}
              onClick={() => setShowConfiguration((prev) => !prev)}
            >
              <Box display="flex" alignItems="center" gap={1}>
                {showConfiguration ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}

                <Typography fontWeight={700} fontSize={14}>
                  Configuration
                </Typography>

                {trigger && <Chip size="small" label={trigger} />}

                {ruleName && (
                  <Typography variant="caption" color="text.secondary">
                    {ruleName}
                  </Typography>
                )}
              </Box>

              <Typography variant="caption" color="text.secondary">
                {showConfiguration ? 'Hide' : 'Show'}
              </Typography>
            </Box>

            {/* BODY */}

            <Collapse in={showConfiguration}>
              <Box p={2}>
                <Stack spacing={2}>
                  {/* CENTRALIZED MODE */}

                  {isCentralizedMode && (
                    <Box display="flex" gap={1}>
                      <Autocomplete
                        fullWidth
                        options={categoryOptions}
                        value={
                          categoryOptions.find((item) => item.id === selectedCategoryId) || null
                        }
                        getOptionLabel={(option) => option.name}
                        onChange={(e, val) => {
                          setSelectedCategoryId(val?.id || '')
                          setSelectedProcessId('')
                          setSelectedActivityId('')
                          resetEditor()
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Category" size="small" />
                        )}
                      />

                      <Autocomplete
                        fullWidth
                        disabled={!selectedCategoryId}
                        options={processOptions}
                        value={processOptions.find((item) => item.id === selectedProcessId) || null}
                        getOptionLabel={(option) => option.name}
                        onChange={(e, val) => {
                          setSelectedProcessId(val?.id || '')
                          setSelectedActivityId('')
                          resetEditor()
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Process" size="small" />
                        )}
                      />

                      <Autocomplete
                        fullWidth
                        disabled={!selectedProcessId}
                        options={activityOptions}
                        value={
                          activityOptions.find((item) => item.id === selectedActivityId) || null
                        }
                        getOptionLabel={(option) => option.name}
                        onChange={(e, val) => {
                          setSelectedActivityId(val?.id || '')
                          resetEditor()
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Activity" size="small" />
                        )}
                      />
                    </Box>
                  )}

                  {/* RULE SETTINGS */}

                  <Box display="flex" gap={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Rule Name"
                      value={ruleName}
                      disabled={!ready}
                      onChange={(e) => setRuleName(e.target.value)}
                    />

                    <Autocomplete
                      sx={{ minWidth: 180 }}
                      options={events}
                      size="small"
                      value={trigger || null}
                      disabled={!ready}
                      onChange={(e, val) => setTrigger(val)}
                      renderInput={(params) => <TextField {...params} label="Trigger" />}
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        label="Expiry"
                        value={expiryDateTime ? dayjs(expiryDateTime) : null}
                        disabled={!ready}
                        onChange={(val) => setExpiryDateTime(val ? val.toISOString() : '')}
                        format="DD MMM YYYY"
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: {
                              minWidth: 180,
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Stack>
              </Box>
            </Collapse>
          </Paper>

          {/* ===================================================== */}
          {/* MAIN */}
          {/* ===================================================== */}

          <Box
            display="flex"
            gap={1}
            sx={{
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* ===================================================== */}
            {/* LEFT */}
            {/* ===================================================== */}

            <Paper
              sx={{
                ...cardSX,
                flex: 1.2,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
              }}
            >
              {/* HEADER */}

              <Box
                px={2}
                py={1}
                borderBottom="1px solid"
                borderColor="divider"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontWeight={700}>DSL Editor</Typography>

                <Tooltip title="Format DSL">
                  <IconButton onClick={() => formatDSL(dsl)}>
                    <FormatAlignRightIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* MONACO */}

              <Box sx={{ flex: 1 }}>
                <DSLMonacoEditor
                  value={dsl}
                  onChange={(value) => {
                    setDsl(value || '')
                    setIsValid(false)
                  }}
                  variables={variables}
                  functions={fns}
                  routes={routes}
                />
              </Box>
            </Paper>

            {/* ===================================================== */}
            {/* RIGHT */}
            {/* ===================================================== */}

            <Paper
              sx={{
                ...cardSX,
                width: {
                  xs: 240,
                  sm: 320,
                  md: 400,
                },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Tabs
                value={rightTab}
                onChange={(e, val) => setRightTab(val)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 42,

                  '& .MuiTab-root': {
                    minHeight: 42,
                    textTransform: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    px: 1.5,
                  },

                  '& .MuiTabs-scrollButtons': {
                    width: 28,
                  },
                }}
              >
                <Tab label="AI Assist" />
                <Tab label="Examples" />
                <Tab label="Variables" />
                <Tab label="Votes" />
                <Tab label="Functions" />
              </Tabs>

              <Box
                p={2}
                sx={{
                  flex: 1,
                  overflow: 'auto',
                }}
              >
                {/* EXAMPLES */}

                {rightTab === 1 && (
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: '#0f172a',
                      color: '#e2e8f0',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      fontSize: 13,
                      borderRadius: 3,
                    }}
                  >
                    {examples}
                  </Paper>
                )}

                {/* VARIABLES */}

                {rightTab === 2 && (
                  <Fade in>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {variables.length === 0 && (
                        <Typography color="text.secondary">No variables available</Typography>
                      )}

                      {variables.map((item) => (
                        <Chip
                          key={item}
                          label={item}
                          clickable
                          onClick={() => copyVariable(item)}
                          deleteIcon={<ContentCopyIcon />}
                          onDelete={() => copyVariable(item)}
                        />
                      ))}
                    </Box>
                  </Fade>
                )}

                {/* VOTES */}

                {rightTab === 3 && (
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {routes.length === 0 && (
                      <Typography color="text.secondary">No votes available</Typography>
                    )}

                    {routes.map((item) => (
                      <Chip
                        key={item}
                        label={item}
                        clickable
                        onClick={() => copyVariable(item)}
                        deleteIcon={<ContentCopyIcon />}
                        onDelete={() => copyVariable(item)}
                      />
                    ))}
                  </Box>
                )}

                {/* FUNCTIONS */}

                {rightTab === 4 && <FunctionList fns={fns} copyVariable={copyVariable} />}

                {rightTab === 0 && (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box>
                      <Typography fontWeight={700} fontSize={14} mb={0.5}>
                        AI Rule Generator
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Describe rule in plain English
                      </Typography>
                    </Box>

                    <TextField
                      fullWidth
                      multiline
                      minRows={6}
                      maxRows={10}
                      value={prompts}
                      disabled={!ready}
                      onChange={(e) => {
                        setPrompts(e.target.value)
                        setDslGenError(null)
                      }}
                      placeholder={`Write your prompt here...
      
If amount greater than 1 lakh reject amount and send back.`}
                    />

                    <Stack direction="row" spacing={1}>
                      <LoadingButton
                        loading={generatingDSL}
                        variant="contained"
                        startIcon={<AutoAwesomeIcon />}
                        disabled={!prompts}
                        onClick={generateDSLOfPrompts}
                        fullWidth
                        sx={{
                          borderRadius: 2,
                          background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                        }}
                      >
                        Generate DSL
                      </LoadingButton>

                      {generatingDSL && (
                        <Button variant="outlined" onClick={cancelGenerateDSL}>
                          Cancel
                        </Button>
                      )}
                    </Stack>
                    {dslGenError && (
                      <Typography variant="p" color="error" fontSize={14}>
                        {dslGenError}
                      </Typography>
                    )}

                    {timeTaken > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Generated in {Math.round(timeTaken)} sec
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>

          {/* ERROR */}

          {error && (
            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca',
              }}
            >
              <Typography color="error">{error}</Typography>
            </Paper>
          )}
        </Box>

        {/* ===================================================== */}
        {/* FOOTER */}
        {/* ===================================================== */}

        <Box
          px={2}
          py={1.2}
          borderTop="1px solid"
          borderColor="divider"
          bgcolor="background.paper"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {/* LEFT */}

          <Stack direction="row" spacing={1}>
            {isEditMode && selectedRuleId && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => handleDelete(selectedRuleId)}
              >
                Delete
              </Button>
            )}

            {isEditMode && selectedRuleId && processId && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<PlayArrowRoundedIcon />}
                onClick={() => handleRunRule(selectedRuleId)}
              >
                Run Rule
              </Button>
            )}
          </Stack>

          {/* RIGHT */}

          <Stack direction="row" spacing={1}>
            <Button onClick={() => onClose(false)}>Cancel</Button>

            <LoadingButton
              loading={isValidating}
              variant="outlined"
              disabled={!ready || !dsl}
              onClick={handleValidate}
            >
              Validate
            </LoadingButton>

            <LoadingButton
              loading={isSaving}
              variant="contained"
              disabled={!ready || !isValid || !dsl}
              onClick={handleSave}
              sx={{
                borderRadius: 2,
              }}
            >
              {isEditMode ? 'Update Rule' : 'Save Rule'}
            </LoadingButton>
          </Stack>
        </Box>
      </Dialog>

      <ConfirmDialog />
    </>
  )
}

export default RuleEditorDialog
